import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GruposService } from 'src/app/core/services/groups/grupos.service';
import { MandoInventarioModel } from 'src/app/core/models/groups/groups-model';
import { MODAL_DIALOG_TYPES } from 'src/app/core/utils/modal-dialog-types';
import { ModalAceptarCancelarComponent } from '../../modal/modal-aceptar-cancelar/modal-aceptar-cancelar.component';
import { DetalleMandoComponent } from './detalle-mando/detalle-mando.component';

@Component({
	selector: 'app-inventario-mandos',
	templateUrl: './inventario-mandos.component.html',
	styleUrls: ['./inventario-mandos.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: false
})
export class InventarioMandosComponent implements OnInit {

	mandos: MandoInventarioModel[] = [];
	filteredMandos: MandoInventarioModel[] = [];
	searchTerm: string = '';
	loading: boolean = false;

	sortColumn: string = 'idMando';
	sortDirection: 'asc' | 'desc' = 'asc';

	estadoLabels: { [key: number]: string } = {
		0: 'Libre',
		1: 'Asignado',
		2: 'Perdido',
		3: 'Estropeado'
	};

	estadoColors: { [key: number]: string } = {
		0: '#28a745',
		1: '#3366ff',
		2: '#ff9f43',
		3: '#ff3d71'
	};

	estadoOptions = [
		{ value: 0, label: 'Libre' },
		{ value: 1, label: 'Asignado' },
		{ value: 2, label: 'Perdido' },
		{ value: 3, label: 'Estropeado' }
	];

	get totalMandos(): number { return this.mandos.length; }
	get mandosAsignados(): number { return this.mandos.filter(m => m.estado === 1).length; }
	get mandosSinAsignar(): number { return this.mandos.filter(m => m.estado === 0).length; }
	get mandosPerdidos(): number { return this.mandos.filter(m => m.estado === 2).length; }
	get mandosEstropeados(): number { return this.mandos.filter(m => m.estado === 3).length; }

	constructor(
		public activeModal: NgbActiveModal,
		private gruposService: GruposService,
		private modalService: NgbModal
	) {}

	ngOnInit() {
		this.loadMandos();
	}

	closeModal() {
		this.activeModal.close();
	}

	loadMandos() {
		this.loading = true;
		this.gruposService.getAllMandosInventario().subscribe({
			next: (res) => {
				if (res && res.data) {
					this.mandos = res.data;
					this.applyFilter();
				}
				this.loading = false;
			},
			error: () => {
				this.loading = false;
			}
		});
	}

	applyFilter() {
		const term = this.searchTerm.toLowerCase().trim();
		if (!term) {
			this.filteredMandos = [...this.mandos];
		} else {
			this.filteredMandos = this.mandos.filter(m =>
				m.idMando?.toLowerCase().includes(term) ||
				m.mac?.toLowerCase().includes(term) ||
				m.centerName?.toLowerCase().includes(term)
			);
		}
		this.applySorting();
	}

	onSearch() {
		this.applyFilter();
	}

	sort(column: string) {
		if (this.sortColumn === column) {
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = column;
			this.sortDirection = 'asc';
		}
		this.applySorting();
	}

	private applySorting() {
		const dir = this.sortDirection === 'asc' ? 1 : -1;
		this.filteredMandos.sort((a: any, b: any) => {
			const valA = a[this.sortColumn] ?? '';
			const valB = b[this.sortColumn] ?? '';
			if (valA < valB) return -1 * dir;
			if (valA > valB) return 1 * dir;
			return 0;
		});
	}

	getSortIcon(column: string): string {
		if (this.sortColumn !== column) return '↕';
		return this.sortDirection === 'asc' ? '↑' : '↓';
	}

	getBatteryIcon(battery: number): string {
		if (battery >= 75) return '🔋';
		if (battery >= 40) return '🔋';
		if (battery >= 15) return '🪫';
		return '🪫';
	}

	getBatteryClass(battery: number): string {
		if (battery >= 75) return 'battery-high';
		if (battery >= 40) return 'battery-medium';
		if (battery >= 15) return 'battery-low';
		return 'battery-critical';
	}

	verDetalle(mando: MandoInventarioModel) {
		const modalRef = this.modalService.open(DetalleMandoComponent, {
			scrollable: true,
			windowClass: MODAL_DIALOG_TYPES.W50,
		});
		modalRef.componentInstance.idMando = mando.idMando;
	}

	eliminarMando(mando: MandoInventarioModel) {
		const modalRef = this.modalService.open(ModalAceptarCancelarComponent, {
			scrollable: true,
			windowClass: MODAL_DIALOG_TYPES.W35,
		});
		modalRef.componentInstance.mensaje =
			`¿Estás seguro de que quieres eliminar el mando ${mando.idMando}? Se eliminarán todas sus relaciones con estudiantes y grupos.`;

		modalRef.result.then((result: string) => {
			if (result === 'aceptar') {
				this.gruposService.deleteMandoCompleto(mando.idMando).subscribe({
					next: () => {
						this.loadMandos();
					}
				});
			}
		}).catch(() => {});
	}

	cambiarEstado(mando: MandoInventarioModel, nuevoEstado: number) {
		if (nuevoEstado === mando.estado) return;
		const estadoAnterior = mando.estado;
		mando.estado = nuevoEstado;
		this.gruposService.updateMandoEstado(mando.idMando, nuevoEstado).subscribe({
			next: (res) => { console.log('Estado actualizado:', res); },
			error: (err) => {
				console.error('Error al actualizar estado:', err);
				mando.estado = estadoAnterior;
			}
		});
	}

	cambiarFechaEntrega(mando: MandoInventarioModel, event: Event) {
		const input = event.target as HTMLInputElement;
		const fecha = input.value;
		const fechaAnterior = mando.fechaEntrega;
		mando.fechaEntrega = fecha ? new Date(fecha + 'T00:00:00') : null;
		this.gruposService.updateMandoFechaEntrega(mando.idMando, fecha).subscribe({
			next: (res) => { console.log('Fecha actualizada:', res); },
			error: (err) => {
				console.error('Error al actualizar fecha:', err);
				mando.fechaEntrega = fechaAnterior;
			}
		});
	}

	toISODate(date: Date | string | null): string {
		if (!date) return '';
		const d = new Date(date);
		return d.toISOString().split('T')[0];
	}

	formatDate(date: Date | string | null): string {
		if (!date) return '—';
		const d = new Date(date);
		return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
	}
}
